/**
 * Menu - tela inicial, opcoes, controles e pausa (secao 4.18 do ARCHITECTURE.md).
 *
 * Este modulo tambem e o dono da TELA DE CARREGAMENTO (#boot-screen /
 * #boot-progress / #boot-bar). O main.js escreve nesses ids desde a PRIMEIRA
 * etapa do boot, mas o Menu so e construido na penultima - por isso a tela de
 * carregamento e criada no carregamento do MODULO (o import acontece antes de
 * boot() rodar) e o construtor apenas confere que ela existe. Assim a barra
 * aparece de verdade desde a primeira etapa, e nao no final.
 *
 * O Menu NAO e registrado no engine (ver main.js): ele nao tem update(). Tudo
 * reage a eventos - GAME_STATE, pointerlockchange e teclado - e a animacao fica
 * por conta do CSS.
 */

/**
 * Registro de cenarios. Vem de src/world/maps/ para o menu nunca anunciar um
 * mapa que nao existe mais.
 */
import { MAPS } from '../world/maps/index.js';


/* -------------------------------------------------------------------------- */
/* tela de carregamento                                                        */
/*                                                                             */
/* ACENTUACAO: identificador e comentario ficam em ASCII, mas TEXTO QUE APARECE */
/* NA TELA leva acento correto de portugues. Nas strings o acento entra como    */
/* escape unicode (\u00c7 = C-cedilha, \u00c3 = A-til, \u00d5 = O-til, \u00da = */
/* U-agudo, \u00cd = I-agudo, \u00c2 = A-circunflexo, \u00ca = E-circunflexo,   */
/* \u00d3 = O-agudo, \u00c1 = A-agudo): o arquivo continua ASCII no disco e o   */
/* jogador ve "OPERACAO" escrito certo. Interface em pt-BR sem acento le como   */
/* placeholder que ninguem revisou, e caixa alta NAO dispensa acento.           */
/* -------------------------------------------------------------------------- */
function ensureBootScreen() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('boot-screen')) return;
  const root = document.getElementById('ui-root') || document.body;
  if (!root) return;

  const el = document.createElement('div');
  el.id = 'boot-screen';
  // A informacao TECNICA do projeto vive aqui, na tela de carregamento, e nao no
  // rodape da tela-titulo: quem esta na tela inicial e jogador, nao avaliador de
  // codigo, e nenhum jogo comercial anuncia a biblioteca que usou na capa.
  el.innerHTML =
    '<div class="boot-inner">' +
      '<div class="boot-mark">SIMULA\u00c7\u00c3O T\u00c1TICA</div>' +
      '<div class="boot-title">OPERA\u00c7\u00c3O<br><span>BLACKOUT</span></div>' +
      '<div class="boot-sub">MONTANDO SISTEMAS DE COMBATE</div>' +
      '<div class="boot-track"><div id="boot-bar"></div></div>' +
      '<div id="boot-progress">INICIANDO</div>' +
      '<div class="boot-note">WEBGL / THREE.JS - GEOMETRIA, TEXTURA E SOM ' +
      'GERADOS EM C\u00d3DIGO, SEM ASSETS EXTERNOS</div>' +
    '</div>';
  root.appendChild(el);
}

// efeito de import: garante a tela de carregamento no primeiro frame possivel
try {
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ensureBootScreen, { once: true });
    } else {
      ensureBootScreen();
    }
  }
} catch (err) {
  console.warn('[Menu] nao foi possivel criar a tela de carregamento cedo:', err);
}

/* -------------------------------------------------------------------------- */
/* dados de tela                                                               */
/* -------------------------------------------------------------------------- */

/** Espelha DEFAULT_BINDINGS de src/core/Input.js em rotulos legiveis. */
const CONTROLS = [
  ['MOVER', 'W A S D'],
  ['CORRER', 'SHIFT'],
  ['AGACHAR', 'CTRL / C'],
  ['PULAR', 'ESPA\u00c7O'],
  ['ATIRAR', 'MOUSE ESQ'],
  ['MIRAR', 'MOUSE DIR'],
  ['RECARREGAR', 'R'],
  ['ESCOLHER ARMA', '1 - 7'],
  ['PERCORRER ARMAS', 'X / RODA'],
  ['ARMA ANTERIOR', 'Q'],
  ['CORPO A CORPO', 'V'],
  ['GRANADA', 'G'],
  ['INTERAGIR', 'F'],
  ['LANTERNA', 'L'],
  ['PLACAR', 'TAB'],
  ['PAUSAR', 'ESC']
];

const pct = (v) => Math.round(v * 100) + '%';

/**
 * Descritores das opcoes. get() le do Settings; set() aplica AO VIVO.
 * Quando a mudanca exige recalcular a projecao ou o tamanho do buffer
 * (fov e qualidade), chamamos engine.resize() - e a API publica que existe
 * para isso, nenhum arquivo do esqueleto e tocado.
 */
const OPTIONS = [
  { group: 'V\u00cdDEO' },
  { type: 'quality', label: 'QUALIDADE' },
  {
    type: 'slider', id: 'fov', label: 'CAMPO DE VIS\u00c3O', min: 65, max: 140, step: 1,
    get: (s) => s.fov,
    set: (ctx, v) => { ctx.settings.set('fov', Math.round(v)); ctx.engine.resize(); },
    fmt: (v) => String(Math.round(v))
  },
  { type: 'toggle', id: 'showFps', label: 'MOSTRAR FPS' },

  { group: 'MIRA E C\u00c2MERA' },
  {
    type: 'slider', id: 'sensitivity', label: 'SENSIBILIDADE', min: 0.2, max: 8, step: 0.05,
    get: (s) => s.sensitivity * 1000,
    set: (ctx, v) => ctx.settings.set('sensitivity', v / 1000),
    fmt: (v) => v.toFixed(2)
  },
  {
    type: 'slider', id: 'adsSensitivityScale', label: 'SENSIBILIDADE NA MIRA',
    min: 0.2, max: 1.2, step: 0.05, fmt: (v) => v.toFixed(2)
  },
  { type: 'toggle', id: 'invertY', label: 'INVERTER EIXO Y' },
  { type: 'slider', id: 'cameraBob', label: 'BALAN\u00c7O DA C\u00c2MERA', min: 0, max: 1.5, step: 0.05, fmt: pct },
  { type: 'slider', id: 'screenShake', label: 'TREMOR DA TELA', min: 0, max: 1.5, step: 0.05, fmt: pct },
  { type: 'toggle', id: 'crosshairEnabled', label: 'MIRA NA TELA' },

  { group: '\u00c1UDIO' },
  { type: 'slider', id: 'masterVolume', label: 'VOLUME GERAL', min: 0, max: 1, step: 0.02, fmt: pct },
  { type: 'slider', id: 'sfxVolume', label: 'EFEITOS', min: 0, max: 1, step: 0.02, fmt: pct },
  { type: 'slider', id: 'musicVolume', label: 'AMBIENTE', min: 0, max: 1, step: 0.02, fmt: pct }
];

/* -------------------------------------------------------------------------- */

export class Menu {
  constructor(ctx) {
    this.ctx = ctx;

    ensureBootScreen();

    // 'boot' e nao 'menu': o main.js emite GAME_STATE {menu} ao terminar o boot
    // e esse primeiro evento tem que ABRIR o menu, nao ser descartado como igual.
    this.state = 'boot';
    this.page = 'main';
    this._returnPage = 'main';
    this._controls = [];
    this._offs = [];
    this._syncing = false;
    this._lastToggleT = 0;

    this.root = null;
    this._accountEl = null;
    this._build();
  }

  async init() {
    const { events, EVENTS: EV } = this.ctx;
    this._offs.push(events.on(EV.GAME_STATE, (p) => this._onState(p?.state)));

    this._onLockChange = () => {
      const locked = document.pointerLockElement === this.ctx.canvas;
      document.body.classList.toggle('is-locked', locked);
      // Perder o mouse com a partida rolando (ESC do browser, troca de aba)
      // significa que o jogador quer parar: pausa em vez de ficar rodando cego.
      if (!locked && this.state === 'playing') this._pause();
    };
    document.addEventListener('pointerlockchange', this._onLockChange);

    this._onKeyDown = (e) => {
      if (e.code !== 'Escape') return;
      // Com o mouse capturado quem trata o ESC e o proprio browser (ele solta o
      // pointer lock); reagir aqui tambem causaria pausa e retomada no mesmo toque.
      if (this.ctx.input?.locked) return;
      if (this.state === 'paused') {
        if (this.page === 'pause') this._resume();
        else this._show('pause');
        e.preventDefault();
      } else if (this.state === 'menu' && this.page !== 'main') {
        this._show('main');
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', this._onKeyDown);

    this._offs.push(events.on(EV.AUTH_CHANGED, () => this._renderAccount()));

    this._offs.push(this.ctx.settings.onChange(() => this._syncOptions()));
    this._syncOptions();
    this._renderAccount();
  }

  /* ===================================================================== */
  /* construcao do DOM                                                      */
  /* ===================================================================== */
  _build() {
    const uiRoot = document.getElementById('ui-root') || document.body;

    const root = document.createElement('div');
    root.id = 'menu-root';

    const veil = document.createElement('div');
    veil.className = 'menu-veil';
    root.appendChild(veil);

    const panel = document.createElement('div');
    panel.className = 'menu-panel';
    root.appendChild(panel);
    this.panel = panel;

    panel.appendChild(this._pageMain());
    panel.appendChild(this._pageOptions());
    panel.appendChild(this._pageControls());
    panel.appendChild(this._pagePause());
    panel.appendChild(this._pageMaps());

    // RODAPE EM UM TRILHO SO. Antes eram dois elementos absolutos: creditos com
    // left de 112px e versao com right de 40px - o mesmo trilho com margens
    // diferentes nas duas pontas -, e como um usa entrelinha 1.8 e o outro a
    // normal, as duas linhas-base saiam 2px fora de registro. O flex com
    // align-items:flex-end nivela as duas pontas independente da entrelinha, e a
    // mesma clamp nos dois lados fecha a simetria.
    // A copy tambem mudou: o rodape anunciava a stack tecnica ("PROTOTIPO
    // TECNICO - WEBGL / THREE.JS") na tela mais importante do jogo, que e copy de
    // README e nao de produto. A informacao tecnica foi para a tela de
    // carregamento; aqui fica ficcao, no mesmo lugar e no mesmo tamanho.
    const foot = document.createElement('div');
    foot.className = 'menu-foot';

    const credits = document.createElement('div');
    credits.className = 'menu-credits';
    credits.innerHTML =
      'FOR\u00c7A-TAREFA 7 - COMANDO REGIONAL<br>' +
      'ACESSO RESTRITO - N\u00cdVEL 3';
    foot.appendChild(credits);

    const version = document.createElement('div');
    version.className = 'menu-version';
    version.textContent = 'BUILD 0.1';
    foot.appendChild(version);

    root.appendChild(foot);

    uiRoot.appendChild(root);
    this.root = root;
    this._show('main');
  }

  _page(name) {
    const p = document.createElement('section');
    p.className = 'menu-page';
    p.dataset.page = name;
    return p;
  }

  _button(label, cls, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn' + (cls ? ' ' + cls : '');
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  _pageMain() {
    const p = this._page('main');

    const head = document.createElement('div');
    head.innerHTML =
      '<div class="menu-eyebrow">OPERA\u00c7\u00c3O</div>' +
      '<h1 class="menu-title">BLACKOUT</h1>' +
      '<div class="menu-rule"></div>' +
      // duas linhas, dois blocos: com <br> a linha ainda podia quebrar sozinha e
      // deixava orfao ("... QUARTEIRAO / LESTE"); cada bloco e indivisivel em
      // tela larga (ver .menu-sub-line no hud.css)
      '<div class="menu-sub">' +
        '<div class="menu-sub-line">SETOR 7 - QUARTEIR\u00c3O LESTE</div>' +
        '<div class="menu-sub-line">CONTEN\u00c7\u00c3O HOSTIL SOB A \u00daLTIMA LUZ DO DIA</div>' +
      '</div>';
    p.appendChild(head);

    const actions = document.createElement('div');
    actions.className = 'menu-actions';
    // "JOGAR" virou "CAMPANHA" porque agora existem DOIS modos. Rotulo generico
    // com duas opcoes ao lado e o jeito mais rapido de o jogador clicar no errado.
    actions.appendChild(this._button('CAMPANHA', 'primary', () => this._startGame()));
    actions.appendChild(this._button('MULTIJOGADOR', '', () => this._openMultiplayer()));
    actions.appendChild(this._button('RANKING', '', () => this._openRanking()));
    actions.appendChild(this._button('MAPA', '', () => { this._returnPage = 'main'; this._show('maps'); }));
    actions.appendChild(this._button('OP\u00c7\u00d5ES', '', () => { this._returnPage = 'main'; this._show('options'); }));
    actions.appendChild(this._button('CONTROLES', '', () => { this._returnPage = 'main'; this._show('controls'); }));
    p.appendChild(actions);

    // Linha da conta: quem esta logado, ou o convite para entrar. Fica no menu e
    // nao so no lobby porque o jogador precisa saber COM QUAL apelido vai jogar
    // antes de entrar na partida - descobrir isso no placar final ja e tarde.
    const account = document.createElement('div');
    account.className = 'menu-account';
    p.appendChild(account);
    this._accountEl = account;

    return p;
  }

  /** Redesenha a linha da conta. Chamado no boot e a cada AUTH_CHANGED. */
  _renderAccount() {
    const el = this._accountEl;
    if (!el) return;
    el.textContent = '';

    const user = this.ctx.session?.user || null;
    const label = document.createElement('span');
    label.className = 'menu-account-label';

    if (user) {
      label.textContent = 'CONECTADO COMO ';
      const nick = document.createElement('strong');
      nick.className = 'menu-account-nick';
      nick.textContent = user.nickname || user.name || '';
      label.appendChild(nick);
      el.appendChild(label);
      el.appendChild(this._button('SAIR DA CONTA', 'link', () => this.ctx.session?.clear?.()));
    } else {
      label.textContent = 'JOGANDO COMO VISITANTE';
      el.appendChild(label);
      el.appendChild(this._button('ENTRAR OU CRIAR CONTA', 'link', () => this.ctx.auth?.open?.('login')));
    }
  }

  /** Multijogador exige conta: sem login, abre o cadastro em vez do lobby. */
  _openMultiplayer() {
    if (this._debounced()) return;
    this.ctx.audio?.resume?.();
    if (!this.ctx.session?.isLoggedIn) {
      this.ctx.auth?.open?.('register');
      return;
    }
    this.ctx.lobby?.open?.();
  }

  _openRanking() {
    if (this._debounced()) return;
    this.ctx.ranking?.open?.();
  }

  /**
   * Escolha de cenario. Trocar de mapa RECARREGA a pagina de proposito: o
   * cenario e construido uma unica vez no boot (geometria procedural, BVH de
   * colisao, mapa de sombra), e reconstruir isso ao vivo seria cirurgia com
   * muito mais risco do que um recarregamento de dois segundos.
   */
  _pageMaps() {
    const p = this._page('maps');
    p.innerHTML =
      '<div class="menu-eyebrow">TEATRO DE OPERA\u00c7\u00d5ES</div>' +
      '<h2 class="menu-h2">MAPA</h2>' +
      '<div class="menu-rule"></div>';

    const lista = document.createElement('div');
    lista.className = 'menu-maps';
    this._mapButtons = [];

    for (const mapa of MAPS) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'btn map-item';
      item.dataset.map = mapa.id;
      const nome = document.createElement('span');
      nome.className = 'map-name';
      nome.textContent = mapa.name;
      const sub = document.createElement('span');
      sub.className = 'map-sub';
      sub.textContent = mapa.sub || '';
      item.appendChild(nome);
      item.appendChild(sub);
      item.addEventListener('click', () => this._pickMap(mapa.id));
      lista.appendChild(item);
      this._mapButtons.push(item);
    }
    p.appendChild(lista);

    const actions = document.createElement('div');
    actions.className = 'menu-actions';
    actions.appendChild(this._button('VOLTAR', 'ghost', () => this._show(this._returnPage || 'main')));
    p.appendChild(actions);
    return p;
  }

  _syncMaps() {
    if (!this._mapButtons) return;
    const atual = this.ctx.settings?.mapId || 'setor7';
    for (const b of this._mapButtons) b.classList.toggle('on', b.dataset.map === atual);
  }

  _pickMap(id) {
    if (this._debounced()) return;
    const atual = this.ctx.settings?.mapId || 'setor7';
    if (id === atual) { this._show(this._returnPage || 'main'); return; }
    this.ctx.settings?.set?.('mapId', id);
    this._syncMaps();
    // Sai de qualquer partida antes de recarregar: sem isso a vaga dele fica
    // presa na sala ate o servidor perceber o silencio.
    if (this.ctx.match?.isActive?.()) this.ctx.match.leaveMatch('trocou de mapa');
    const aviso = document.createElement('div');
    aviso.className = 'menu-mapload';
    aviso.textContent = 'CARREGANDO CEN\u00c1RIO...';
    this.root?.appendChild(aviso);
    setTimeout(() => { try { window.location.reload(); } catch { /* sem window */ } }, 400);
  }

  _pagePause() {
    const p = this._page('pause');
    p.innerHTML =
      '<div class="menu-eyebrow">OPERA\u00c7\u00c3O EM CURSO</div>' +
      '<h2 class="menu-h2">PAUSADO</h2>' +
      '<div class="menu-rule"></div>';

    const actions = document.createElement('div');
    actions.className = 'menu-actions';
    actions.appendChild(this._button('RETOMAR', 'primary', () => this._resume()));
    actions.appendChild(this._button('OP\u00c7\u00d5ES', '', () => { this._returnPage = 'pause'; this._show('options'); }));
    actions.appendChild(this._button('CONTROLES', '', () => { this._returnPage = 'pause'; this._show('controls'); }));
    actions.appendChild(this._button('SAIR PARA O MENU', 'ghost', () => this._quitToMenu()));
    p.appendChild(actions);

    return p;
  }

  _pageOptions() {
    const p = this._page('options');
    p.innerHTML = '<div class="menu-eyebrow">CONFIGURA\u00c7\u00c3O</div>' +
      '<h2 class="menu-h2">OP\u00c7\u00d5ES</h2><div class="menu-rule"></div>';

    const scroll = document.createElement('div');
    scroll.className = 'opt-scroll';

    for (const opt of OPTIONS) {
      if (opt.group) {
        const g = document.createElement('div');
        g.className = 'opt-group';
        g.textContent = opt.group;
        scroll.appendChild(g);
        continue;
      }
      if (opt.type === 'quality') scroll.appendChild(this._rowQuality(opt));
      else if (opt.type === 'slider') scroll.appendChild(this._rowSlider(opt));
      else if (opt.type === 'toggle') scroll.appendChild(this._rowToggle(opt));
    }

    p.appendChild(scroll);

    const actions = document.createElement('div');
    actions.className = 'menu-actions';
    actions.appendChild(this._button('VOLTAR', 'ghost', () => this._show(this._returnPage)));
    p.appendChild(actions);

    return p;
  }

  _rowQuality(opt) {
    const row = document.createElement('div');
    row.className = 'opt-row seg';

    const label = document.createElement('span');
    label.className = 'opt-label';
    label.textContent = opt.label;
    row.appendChild(label);

    const group = document.createElement('div');
    group.className = 'seg-group';

    const presets = this.ctx.settings.presets || {};
    const order = ['low', 'medium', 'high', 'ultra'].filter((k) => presets[k]);
    const buttons = [];

    for (const key of order) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'seg-btn';
      b.dataset.quality = key;
      b.textContent = String(presets[key].label || key).toUpperCase();
      b.addEventListener('click', () => {
        this.ctx.settings.setQuality(key);
        // resolutionScale muda com o preset: sem resize o buffer fica no antigo
        this.ctx.engine.resize();
        this._syncOptions();
      });
      group.appendChild(b);
      buttons.push(b);
    }

    row.appendChild(group);
    this._controls.push({ kind: 'quality', buttons });
    return row;
  }

  _rowSlider(opt) {
    const row = document.createElement('div');
    row.className = 'opt-row';

    const label = document.createElement('span');
    label.className = 'opt-label';
    label.textContent = opt.label;

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(opt.min);
    input.max = String(opt.max);
    input.step = String(opt.step);

    const value = document.createElement('span');
    value.className = 'opt-value';

    const fmt = opt.fmt || ((v) => v.toFixed(2));
    const apply = opt.set || ((ctx, v) => ctx.settings.set(opt.id, v));

    input.addEventListener('input', () => {
      if (this._syncing) return;
      const v = parseFloat(input.value);
      if (!Number.isFinite(v)) return;
      value.textContent = fmt(v);
      apply(this.ctx, v);
    });

    row.appendChild(label);
    row.appendChild(input);
    row.appendChild(value);

    this._controls.push({ kind: 'slider', opt, input, value, fmt });
    return row;
  }

  _rowToggle(opt) {
    const row = document.createElement('div');
    row.className = 'opt-row';

    const label = document.createElement('span');
    label.className = 'opt-label';
    label.textContent = opt.label;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tgl';
    btn.setAttribute('aria-label', opt.label);

    const value = document.createElement('span');
    value.className = 'opt-value';

    btn.addEventListener('click', () => {
      const now = !this._readToggle(opt);
      if (opt.set) opt.set(this.ctx, now);
      else this.ctx.settings.set(opt.id, now);
      this._syncOptions();
    });

    row.appendChild(label);
    row.appendChild(btn);
    row.appendChild(value);

    this._controls.push({ kind: 'toggle', opt, btn, value });
    return row;
  }

  _readToggle(opt) {
    return opt.get ? !!opt.get(this.ctx.settings) : !!this.ctx.settings[opt.id];
  }

  _pageControls() {
    const p = this._page('controls');
    p.innerHTML = '<div class="menu-eyebrow">REFER\u00caNCIA</div>' +
      '<h2 class="menu-h2">CONTROLES</h2><div class="menu-rule"></div>';

    const list = document.createElement('div');
    list.className = 'ctl-list';
    for (const [act, key] of CONTROLS) {
      const row = document.createElement('div');
      row.className = 'ctl-row';
      const a = document.createElement('span');
      a.className = 'ctl-act';
      a.textContent = act;
      const k = document.createElement('span');
      k.className = 'ctl-key';
      k.textContent = key;
      row.appendChild(a);
      row.appendChild(k);
      list.appendChild(row);
    }
    p.appendChild(list);

    const actions = document.createElement('div');
    actions.className = 'menu-actions';
    actions.appendChild(this._button('VOLTAR', 'ghost', () => this._show(this._returnPage)));
    p.appendChild(actions);

    return p;
  }

  /* ===================================================================== */
  /* navegacao                                                              */
  /* ===================================================================== */
  _show(page) {
    this.page = page;
    const pages = this.panel.querySelectorAll('.menu-page');
    for (const el of pages) el.classList.toggle('active', el.dataset.page === page);
    this.panel.classList.toggle('wide', page === 'options' || page === 'controls' || page === 'maps');
    this.root.classList.toggle('pause', page === 'pause');
    if (page === 'options') this._syncOptions();
    if (page === 'maps') this._syncMaps();
  }

  _open(page) {
    this._show(page);
    this.root.classList.add('open');
  }

  _close() {
    this.root.classList.remove('open');
  }

  _syncOptions() {
    if (!this._controls.length) return;
    this._syncing = true;
    const s = this.ctx.settings;
    try {
      for (const c of this._controls) {
        if (c.kind === 'quality') {
          for (const b of c.buttons) b.classList.toggle('on', b.dataset.quality === s.quality);
        } else if (c.kind === 'slider') {
          const raw = c.opt.get ? c.opt.get(s) : s[c.opt.id];
          const v = Number.isFinite(raw) ? raw : c.opt.min;
          c.input.value = String(v);
          c.value.textContent = c.fmt(v);
        } else if (c.kind === 'toggle') {
          const on = this._readToggle(c.opt);
          c.btn.classList.toggle('on', on);
          c.value.textContent = on ? 'SIM' : 'N\u00c3O';
        }
      }
    } catch (err) {
      console.warn('[Menu] falha ao sincronizar as opcoes:', err);
    }
    this._syncing = false;
  }

  /* ===================================================================== */
  /* estado de jogo                                                         */
  /* ===================================================================== */
  _onState(state) {
    if (!state || state === this.state) return;
    this.state = state;
    const input = this.ctx.input;

    if (state === 'menu') {
      // O mundo continua rodando atras do menu: e ele que da profundidade a tela.
      this.ctx.engine.paused = false;
      if (input) input.enabled = false;
      input?.releaseLock?.();
      this._open('main');
    } else if (state === 'paused') {
      this.ctx.engine.paused = true;
      if (input) input.enabled = false;
      input?.releaseLock?.();
      this._open('pause');
    } else if (state === 'playing') {
      this.ctx.engine.paused = false;
      if (input) input.enabled = true;
      this._close();
      // NAO pedir pointer lock aqui. Parece o lugar obvio, e nao e: este metodo
      // roda em resposta a um EVENTO, nao ao clique. O navegador so concede o
      // lock dentro de um gesto do usuario, entao o pedido falha, o
      // pointerlockchange chega com locked=false, e o _onLockChange logo abaixo
      // le isso como "o jogador perdeu o mouse" e PAUSA a partida que acabou de
      // comecar. Quem pede o lock e quem foi clicado: _startGame (campanha),
      // Lobby._join e Scoreboard (jogar de novo) - todos dentro do gesto.
    } else {
      // gameover / victory: a tela e do HUD, o menu apenas sai da frente
      if (input) input.enabled = false;
      this._close();
    }
  }

  /** Anti-repique: ESC costuma chegar duas vezes (tecla + perda do pointer lock). */
  _debounced() {
    const now = performance.now();
    if (now - this._lastToggleT < 260) return true;
    this._lastToggleT = now;
    return false;
  }

  _startGame() {
    if (this._debounced()) return;
    // Uma partida online no ar impede a campanha: os dois modos disputariam o
    // mesmo jogador e o mesmo cenario.
    if (this.ctx.match?.isActive?.()) this.ctx.match.leaveMatch('trocou para a campanha');
    // resume() e requestLock() precisam sair do gesto do usuario: vem primeiro
    this.ctx.audio?.resume?.();
    // No celular nao ha pointer lock - quem cuida da mira e o TouchControls.
    if (!this.ctx.input?.touchActive && !this.ctx.touch?.supported) this.ctx.input?.requestLock?.();
    this.ctx.engine.paused = false;
    this.ctx.game?.start?.();
    this.ctx.events.emit(this.ctx.EVENTS.GAME_STATE, { state: 'playing' });
  }

  _pause() {
    if (this.state !== 'playing') return;
    if (this._debounced()) return;
    this.ctx.input?.releaseLock?.();
    this.ctx.events.emit(this.ctx.EVENTS.GAME_STATE, { state: 'paused' });
  }

  _resume() {
    if (this.state !== 'paused') return;
    if (this._debounced()) return;
    this.ctx.audio?.resume?.();
    this.ctx.input?.requestLock?.();
    this.ctx.events.emit(this.ctx.EVENTS.GAME_STATE, { state: 'playing' });
  }

  _quitToMenu() {
    this._lastToggleT = 0;
    // Sair da partida ANTES de voltar ao menu. Sem isto o jogador volta para o
    // menu com o NetSync ainda mandando estado a 20 Hz: o boneco dele fica
    // parado na arena levando tiro, e o placar continua correndo sem ele.
    if (this.ctx.match?.isActive?.()) this.ctx.match.leaveMatch('saiu para o menu');
    this.ctx.input?.releaseLock?.();
    this.ctx.events.emit(this.ctx.EVENTS.GAME_STATE, { state: 'menu' });
  }

  dispose() {
    for (const off of this._offs) { try { off(); } catch { /* ignorado */ } }
    this._offs.length = 0;
    if (this._onLockChange) document.removeEventListener('pointerlockchange', this._onLockChange);
    if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown);
    this.root?.remove();
    this._controls.length = 0;
  }
}
